import { inject, Injectable } from '@angular/core';

import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { EditAction } from '@connect-services/client/sdk/api';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { readOnlyAttributes } from '@connect-services/client/services/layer.service';

@Injectable({
    providedIn: 'root',
})
export class EditModeInputEventFacadeService {
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);

    public addOnePropertyUpdate(params: {
        attributeKey: string;
        businessIdentifier: { key: string; value: unknown };
        entityId: string;
        layerId: string;
        newValue: unknown;
        previousValue: unknown;
        projectId: string;
        tableName: string;
    }): void {
        if (this.entityDetailCreationContextService.source()) {
            return;
        }

        const { attributeKey, entityId, layerId, newValue, previousValue, projectId, tableName } = params;

        if (previousValue === newValue) {
            return;
        }

        const before = previousValue === undefined ? {} : { [attributeKey]: previousValue };
        const after = { [attributeKey]: newValue };

        const eventAction: EditEventAction = {
            action: EditAction.UPDATE,
            after,
            before,
            businessIdentifier: params.businessIdentifier,
            entityId,
            layerId,
            tableName,
        };
        this.editModeStateService.addEdit(projectId, { actions: [eventAction] });
    }

    public getBulkUpdateEvent(params: {
        after: Partial<EntityProperties>;
        before: Partial<EntityProperties>;
        businessIdentifier: { key: string; value: unknown };
        entityId: string;
        layerId: string;
        projectId: string;
        tableName: string;
    }): EditEventAction | undefined {
        if (this.entityDetailCreationContextService.source()) {
            return;
        }

        const { layerId, entityId, before, after, tableName } = params;

        if (!after || !Object.keys(after).length) {
            return;
        }

        const changedAfter: Partial<EntityProperties> = {};
        const changedBefore: Partial<EntityProperties> = {};

        for (const key of Object.keys(after)) {
            const newValue = after[key];
            const prevValue = before?.[key];

            if (readOnlyAttributes.includes(key)) {
                changedAfter[key] = newValue;
                changedBefore[key] = newValue;

                continue;
            }

            if (prevValue === newValue) {
                continue;
            }

            changedAfter[key] = newValue;
            if (prevValue !== undefined) {
                changedBefore[key] = prevValue;
            }
        }

        if (!Object.keys(changedAfter).length) {
            return;
        }

        return {
            action: EditAction.UPDATE,
            after: changedAfter,
            before: changedBefore,
            businessIdentifier: params.businessIdentifier,
            entityId,
            layerId,
            tableName,
        };
    }
}
